import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    IconButton,
    InputBase,
    TextField,
    Typography
} from "@mui/material";
import { Search as SearchIcon, Close as CloseIcon, FitnessCenter as FitnessCenterIcon } from "@mui/icons-material";
import { useState, useCallback, useEffect } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { getExercises } from "../../services/ExcersiceService";
import { RTL } from "../../cache/CacheRtl";
import { getMuscleGroupColor } from "../../utils/exerciseUtils";
import { targetMuscles } from "../../enums/TargetMuscles";
import MyDialog from "../shared/Dialog";
import { imageUrl as resizedImageUrl } from "../../utils/imageUrl";

// Build a lookup map for targetMuscle enum value -> Hebrew name
const targetMuscleMap = new Map(targetMuscles.map(item => [item.value, item.name]));

const AddExercisesDialog = ({ isOpen, close, onAdd, title: customTitle, initialTargetMuscle, currentExerciseId, excludeCardio, catalystOnly, hideFilter }) => {
    const [keyword, setKeyword] = useState("");
    const [targetMuscle, setTargetMuscle] = useState(null);
    const [exercises, setExercises] = useState([]);
    const [pageId, setPageId] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedExercises, setSelectedExercises] = useState([]);

    // Track the current fetch params via a revision counter to avoid stale fetches
    const [fetchRevision, setFetchRevision] = useState(0);

    // Reset state when dialog opens
    useEffect(() => {
        if (isOpen) {
            setKeyword("");
            setTargetMuscle(initialTargetMuscle || null);
            setExercises([]);
            setPageId(0);
            setHasMore(true);
            setIsLoading(false);
            setSelectedExercises([]);
            setFetchRevision(r => r + 1);
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset list when keyword or filter changes
    useEffect(() => {
        if (!isOpen) return;
        setExercises([]);
        setPageId(0);
        setHasMore(true);
        setFetchRevision(r => r + 1);
    }, [keyword, targetMuscle]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch first page whenever revision changes (after state has settled)
    useEffect(() => {
        if (!isOpen || fetchRevision === 0) return;
        setIsLoading(true);
        const catalystParam = catalystOnly ? true : excludeCardio ? false : undefined;
        getExercises(0, 20, keyword || undefined, undefined, targetMuscle, undefined, catalystParam, true)
            .then((res) => {
                let content = res.data.content;
                if (excludeCardio) content = content.filter(ex => ex.targetMuscle !== 'CARDIO');
                setExercises(content);
                setPageId(1);
                setHasMore(content.length > 0);
            })
            .catch((e) => {
                console.error("Error fetching exercises:", e);
                setHasMore(false);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [fetchRevision]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchNextPage = useCallback(() => {
        if (isLoading || pageId === 0) return;
        setIsLoading(true);

        const catalystParam = catalystOnly ? true : excludeCardio ? false : undefined;
        getExercises(pageId, 20, keyword || undefined, undefined, targetMuscle, undefined, catalystParam, true)
            .then((res) => {
                let content = res.data.content;
                if (excludeCardio) content = content.filter(ex => ex.targetMuscle !== 'CARDIO');
                setExercises((prev) => [...prev, ...content]);
                setPageId((prev) => prev + 1);
                setHasMore(content.length > 0);
            })
            .catch((e) => {
                console.error("Error fetching exercises:", e);
                setHasMore(false);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [pageId, keyword, targetMuscle, isLoading]);

    const singleSelect = !!currentExerciseId;

    const toggleExercise = (exercise) => {
        if (singleSelect) {
            setSelectedExercises((prev) =>
                prev.some((e) => e.id === exercise.id) ? [] : [exercise]
            );
            return;
        }
        setSelectedExercises((prev) => {
            const isSelected = prev.some((e) => e.id === exercise.id);
            if (isSelected) {
                return prev.filter((e) => e.id !== exercise.id);
            }
            return [...prev, exercise];
        });
    };

    const removeSelected = (exerciseId) => {
        setSelectedExercises((prev) => prev.filter((e) => e.id !== exerciseId));
    };

    const handleAdd = () => {
        if (selectedExercises.length > 0 && onAdd) {
            onAdd(selectedExercises);
            close();
        }
    };

    const getTargetMuscleLabel = (value) => {
        return targetMuscleMap.get(value) || value || '';
    };

    const renderLoader = () => (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
        </Box>
    );

    return (
        <MyDialog
            isOpen={isOpen}
            close={close}
            title={customTitle || "הוסף תרגילים לאימון"}
            showCloseButton
            actions={
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <RTL>
                        <Button
                            variant="contained"
                            onClick={handleAdd}
                            disabled={selectedExercises.length === 0}
                            sx={{
                                backgroundColor: "#57837E",
                                borderRadius: "8px",
                                textTransform: "none",
                                fontWeight: 600,
                                px: 4,
                                "&:hover": { backgroundColor: "#456b67" },
                                "&:disabled": { backgroundColor: "#E2E8F0" }
                            }}
                        >
                            {singleSelect ? 'החלף' : `הוסף ${selectedExercises.length > 0 ? `(${selectedExercises.length})` : ""}`}
                        </Button>
                    </RTL>
                </Box>
            }
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '60vh', direction: 'rtl' }}>
                {/* Search & Filter Row */}
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    mx: 1,
                    mt: 2,
                    mb: 1,
                    flexWrap: 'wrap',
                }}>
                    {/* Search Input */}
                    <Box sx={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        minWidth: 180,
                    }}>
                        <IconButton sx={{ padding: "8px", color: "#64748B" }}>
                            <SearchIcon fontSize="small" />
                        </IconButton>
                        <InputBase
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            sx={{
                                flex: 1,
                                fontSize: "14px",
                                "& input::placeholder": { color: "#94A3B8" }
                            }}
                            placeholder="חיפוש לפי שם תרגיל..."
                            autoFocus
                        />
                    </Box>

                    {/* Category Filter */}
                    {!hideFilter && (
                        <RTL>
                            <Autocomplete
                                disablePortal
                                value={targetMuscle}
                                getOptionLabel={(option) => option?.name || ""}
                                onChange={(event, value) => setTargetMuscle(value || null)}
                                options={excludeCardio ? targetMuscles.filter(m => m.value !== 'CARDIO') : targetMuscles}
                                sx={{
                                    width: 180,
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "#FFFFFF",
                                        borderRadius: "8px",
                                        "& fieldset": { borderColor: "#E2E8F0" }
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "#64748B",
                                        fontSize: "14px"
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="קבוצת שרירים"
                                        size="small"
                                    />
                                )}
                            />
                        </RTL>
                    )}
                </Box>

                {/* Selected Chips */}
                {!singleSelect && selectedExercises.length > 0 && (
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        px: 1,
                        py: 1,
                        borderBottom: '1px solid #E2E8F0'
                    }}>
                        {selectedExercises.map((ex) => (
                            <RTL key={ex.id}>
                                <Chip
                                    label={ex.nameHeb || ex.name}
                                    size="small"
                                    onDelete={() => removeSelected(ex.id)}
                                    deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
                                    sx={{
                                        backgroundColor: '#F1F5F9',
                                        fontSize: '0.75rem',
                                        '& .MuiChip-deleteIcon': {
                                            color: '#94A3B8',
                                            '&:hover': { color: '#64748B' }
                                        }
                                    }}
                                />
                            </RTL>
                        ))}
                    </Box>
                )}

                {/* Exercise List */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 1 }} data-exercises-scroll>
                    <InfiniteScroll
                        initialLoad={false}
                        hasMore={hasMore}
                        loadMore={fetchNextPage}
                        loader={renderLoader()}
                        useWindow={false}
                        getScrollParent={() => document.querySelector('[data-exercises-scroll]')}
                    >
                        {exercises.map((exercise) => {
                            const isSelected = selectedExercises.some((e) => e.id === exercise.id);
                            const isCurrent = currentExerciseId && exercise.id === currentExerciseId;
                            const imageUrl = exercise.imageUrl || exercise.coverUrl;
                            const muscleLabel = getTargetMuscleLabel(exercise.targetMuscle);
                            const muscleColor = getMuscleGroupColor(exercise.targetMuscle);

                            return (
                                <Box
                                    key={exercise.id}
                                    onClick={() => toggleExercise(exercise)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        py: 1,
                                        px: 1,
                                        borderBottom: '1px solid #F1F5F9',
                                        cursor: 'pointer',
                                        borderRadius: '8px',
                                        borderRight: isCurrent ? `3px solid ${muscleColor}` : 'none',
                                        backgroundColor: isSelected ? '#F0FAF8' : isCurrent ? `${muscleColor}08` : 'transparent',
                                        '&:hover': {
                                            backgroundColor: isSelected ? '#E0F5F0' : '#F8FAFC'
                                        },
                                        transition: 'background-color 0.15s ease'
                                    }}
                                >
                                    {!singleSelect && (
                                        <Checkbox
                                            checked={isSelected}
                                            size="small"
                                            sx={{
                                                padding: 0,
                                                color: '#CBD5E1',
                                                '&.Mui-checked': { color: '#57837E' }
                                            }}
                                        />
                                    )}

                                    {/* Thumbnail */}
                                    <Avatar
                                        src={resizedImageUrl(imageUrl, 44)}
                                        variant="rounded"
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            backgroundColor: '#F1F5F9',
                                            borderRadius: '8px',
                                        }}
                                    >
                                        <FitnessCenterIcon sx={{ color: '#CBD5E1', fontSize: 20 }} />
                                    </Avatar>

                                    {/* Name & Category */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Typography sx={{
                                                fontSize: '0.85rem',
                                                fontWeight: isCurrent ? 700 : 500,
                                                color: '#1E293B',
                                                lineHeight: 1.3,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}>
                                                {exercise.nameHeb || exercise.name}
                                            </Typography>
                                            {isCurrent && (
                                                <Chip
                                                    label="תרגיל נוכחי"
                                                    size="small"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: '0.6rem',
                                                        fontWeight: 600,
                                                        backgroundColor: `${muscleColor}15`,
                                                        color: muscleColor,
                                                        border: `1px solid ${muscleColor}30`,
                                                        '& .MuiChip-label': { px: 0.75 }
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        {muscleLabel && (
                                            <Chip
                                                label={muscleLabel}
                                                size="small"
                                                sx={{
                                                    mt: 0.5,
                                                    height: 20,
                                                    fontSize: '0.65rem',
                                                    fontWeight: 600,
                                                    backgroundColor: `${muscleColor}15`,
                                                    color: muscleColor,
                                                    border: `1px solid ${muscleColor}30`,
                                                    '& .MuiChip-label': { px: 1 }
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}

                        {exercises.length === 0 && !hasMore && !isLoading && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    textAlign: 'center',
                                    py: 4,
                                    fontStyle: 'italic'
                                }}
                            >
                                לא נמצאו תרגילים
                            </Typography>
                        )}
                    </InfiniteScroll>
                </Box>
            </Box>
        </MyDialog>
    );
};

export default AddExercisesDialog;
